/**
 * AdminAiKnowledgeSubtab — admin CRUD over Ion knowledge entries.
 *
 * What it surfaces (data from /admin/ion-knowledge):
 *   - topic_id (immutable), title, summary
 *   - is_active flag, version, last-updated timestamp
 *   - keywords (search-only, not in prompt) and applies_to context hint
 *
 * Actions:
 *   - Create new entry  (modal form)
 *   - Edit existing     (modal form, topic_id locked)
 *   - Delete            (HARD delete — permanently removes the row)
 *   - Search-filter     (title + topic_id + keywords)
 *   - Toggle active-only filter
 *
 * Repo-managed mode (ION_KNOWLEDGE_REPO_SYNC, issue #13): when
 * /capabilities reports repo_managed, the content comes from
 * backend/app/api/v1/chat/knowledge_repo/knowledge.json and every write
 * endpoint answers 409, so the subtab renders read-only with a banner
 * (no New/Import, rows open a read-only "View" drawer). Export stays.
 *
 * The runtime serves entries via the [ION KNOWLEDGE] index in the
 * system prompt + the get_knowledge tool — see backend chat/knowledge.py.
 * Edits invalidate the runtime cache server-side, so changes propagate
 * to the next chat turn within ~1 minute even on multi-worker deployments.
 */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { createPortal } from 'preact/compat'
import {
  listKnowledgeEntries,
  listKnowledgeCategories,
  createKnowledgeEntry,
  updateKnowledgeEntry,
  deleteKnowledgeEntry,
  getKnowledgeCapabilities,
  exportKnowledgeBundle,
  importKnowledgeBundle,
} from '../../services/ionKnowledgeService.js'
import { getAdminRole } from '../../state/userState.js'

function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
}

const TOPIC_ID_RE = /^[a-z0-9_-]+$/

// Fallback taxonomy if /categories can't be reached — kept in sync with
// backend/app/api/v1/admin/ion_knowledge.py::KNOWLEDGE_CATEGORIES. Admin-plane
// only; never read by the runtime prompt index or get_knowledge.
const FALLBACK_CATEGORIES = [
  'selection-flow',
  'domain-knowledge',
  'feature-doc',
  'policy',
  'general',
]

// Soft guidance threshold (~1500 tokens at ~4 chars/token) matching
// get_knowledge's own docstring guidance. NOT enforced — the 20,000-char
// Pydantic cap (KnowledgeEntryIn/Update) stays the only hard limit.
const BODY_LENGTH_SOFT_WARN = 6000

export function AdminAiKnowledgeSubtab({ onGoToMySubmissions } = {}) {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [showActiveOnly, setShowActiveOnly] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState('')
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES)
  const [editing, setEditing] = useState(null) // null | 'new' | entry object
  // Approval gate: authors (can't publish directly) get a review notice and
  // their saves queue as proposals instead of going live.
  const [canPublish, setCanPublish] = useState(true)
  // Repo-managed (ION_KNOWLEDGE_REPO_SYNC): read-only, edits go through the
  // repo file in a merge request. `repoFile` names it in the banner.
  const [repoManaged, setRepoManaged] = useState(false)
  const [repoFile, setRepoFile] = useState(null)
  // Non-blocking confirmation shown after a queued submission (replaces the old
  // window.alert) — points the author to the My Submissions tab to track it.
  const [queuedNotice, setQueuedNotice] = useState(null)
  // Cross-env promotion (export/import) — full-admin only, matches the
  // AdminRole.ADMIN gate on the endpoints. `promoBusy` disables both buttons
  // during a transfer; `promoNotice` reports the import result.
  const isFullAdmin = getAdminRole() === 'admin'
  const [promoBusy, setPromoBusy] = useState(false)
  const [promoNotice, setPromoNotice] = useState(null)
  const importInputRef = useRef(null)

  useEffect(() => {
    getKnowledgeCapabilities()
      .then((c) => {
        setCanPublish(c.can_publish_directly !== false)
        setRepoManaged(c.repo_managed === true)
        setRepoFile(c.repo_file || null)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    listKnowledgeCategories()
      .then((c) => {
        if (Array.isArray(c.categories) && c.categories.length > 0) {
          setCategories(c.categories)
        }
      })
      .catch(() => {
        /* keep FALLBACK_CATEGORIES */
      })
  }, [])

  const handleExport = async () => {
    setPromoBusy(true)
    setError(null)
    try {
      const bundle = await exportKnowledgeBundle()
      const stamp = new Date().toISOString().slice(0, 10)
      const blob = new Blob([JSON.stringify(bundle, null, 2)], {
        type: 'application/json',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ion-knowledge-${stamp}.json`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      setPromoNotice(`Exported ${bundle.count} entries to ion-knowledge-${stamp}.json`)
    } catch (e) {
      setError(`Export failed: ${e?.message || 'network'}`)
    } finally {
      setPromoBusy(false)
    }
  }

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0]
    // Reset the input so re-selecting the same file fires onChange again.
    event.target.value = ''
    if (!file) return
    setPromoBusy(true)
    setError(null)
    try {
      const text = await file.text()
      const parsed = JSON.parse(text)
      // Accept either a full export bundle ({entries:[...]}) or a bare array.
      const entries = Array.isArray(parsed) ? parsed : parsed?.entries
      if (!Array.isArray(entries) || entries.length === 0) {
        throw new Error('File has no "entries" array.')
      }
      const ok = window.confirm(
        `Import ${entries.length} knowledge entries into THIS environment?\n\n` +
          `Existing entries with the same topic_id are overwritten (content + ` +
          `version bump). Nothing is deleted. This is the staging→prod ` +
          `promotion path — make sure you're on the intended environment.`,
      )
      if (!ok) return
      const result = await importKnowledgeBundle(entries)
      setPromoNotice(
        `Imported: ${result.created.length} created, ${result.updated.length} updated ` +
          `(${result.total} total).`,
      )
      refresh()
    } catch (e) {
      setError(`Import failed: ${e?.message || 'network'}`)
    } finally {
      setPromoBusy(false)
    }
  }

  const refresh = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await listKnowledgeEntries({ includeInactive: true })
      setEntries(data.entries || [])
    } catch (e) {
      setError(e?.message || 'network')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return entries.filter((e) => {
      if (showActiveOnly && !e.is_active) return false
      if (categoryFilter && (e.category || 'general') !== categoryFilter) return false
      if (!q) return true
      return (
        e.topic_id.toLowerCase().includes(q) ||
        e.title.toLowerCase().includes(q) ||
        (e.keywords || '').toLowerCase().includes(q)
      )
    })
  }, [entries, search, showActiveOnly, categoryFilter])

  return (
    <div className="admin-aim-subtab-body">
      <header className="admin-aim-header">
        <div>
          <h2 className="admin-aim-title">Knowledge</h2>
          <p className="admin-aim-sub">
            Admin-editable entries the AI surfaces via [ION KNOWLEDGE] in
            its system prompt. Each entry's title + summary is part of the
            cached prompt index; the body is fetched on-demand via
            <code> get_knowledge</code>. Edits invalidate the runtime
            cache automatically.
          </p>
        </div>
        <div className="admin-aim-header-actions">
          {isFullAdmin && (
            <button
              type="button"
              className="admin-ai-btn admin-ai-btn--ghost"
              onClick={handleExport}
              disabled={promoBusy}
              title="Download all entries as a JSON bundle (for staging→prod promotion)"
            >
              ⬇ Export
            </button>
          )}
          {isFullAdmin && !repoManaged && (
            <>
              <button
                type="button"
                className="admin-ai-btn admin-ai-btn--ghost"
                onClick={() => importInputRef.current?.click()}
                disabled={promoBusy}
                title="Import a knowledge bundle into this environment (upsert by topic_id)"
              >
                ⬆ Import
              </button>
              <input
                ref={importInputRef}
                type="file"
                accept="application/json,.json"
                style={{ display: 'none' }}
                onChange={handleImportFile}
              />
            </>
          )}
          {!repoManaged && (
            <button
              type="button"
              className="admin-ai-btn"
              onClick={() => setEditing('new')}
            >
              {canPublish ? '+ New entry' : '+ Propose entry'}
            </button>
          )}
        </div>
      </header>

      {repoManaged && (
        <div
          className="admin-aim-notice-banner admin-aim-repo-managed-banner"
          role="note"
          data-testid="knowledge-repo-managed-banner"
        >
          <span className="admin-aim-notice-text">
            <strong>Read-only.</strong> Ion knowledge on this environment is
            managed in the repository. To change an entry, edit{' '}
            <code>{repoFile || 'backend/app/api/v1/chat/knowledge_repo/knowledge.json'}</code>{' '}
            in a merge request; it is applied on the next deploy.
          </span>
        </div>
      )}

      {promoNotice && (
        <div className="admin-aim-notice-banner" role="status">
          <span className="admin-aim-notice-text">{promoNotice}</span>
          <button
            type="button"
            className="admin-aim-notice-close"
            aria-label="Dismiss"
            onClick={() => setPromoNotice(null)}
          >
            ×
          </button>
        </div>
      )}

      {queuedNotice && (
        <div className="admin-aim-notice-banner" role="status">
          <span className="admin-aim-notice-text">{queuedNotice}</span>
          {onGoToMySubmissions && (
            <button
              type="button"
              className="admin-aim-notice-link"
              onClick={onGoToMySubmissions}
            >
              View my submissions →
            </button>
          )}
          <button
            type="button"
            className="admin-aim-notice-close"
            aria-label="Dismiss"
            onClick={() => setQueuedNotice(null)}
          >
            ×
          </button>
        </div>
      )}

      <div className="admin-aim-toolbar">
        <input
          type="search"
          className="admin-aim-search"
          placeholder="Search by topic_id, title, or keywords…"
          value={search}
          onInput={(e) => setSearch(e.target.value)}
        />
        <label className="admin-aim-checkbox">
          <input
            type="checkbox"
            checked={showActiveOnly}
            onChange={(e) => setShowActiveOnly(e.target.checked)}
          />
          Active only
        </label>
        <select
          className="admin-aim-search"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          title="Filter by admin-plane category (not read by the AI at runtime)"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--ghost"
          onClick={refresh}
        >
          ↻
        </button>
        <span className="admin-aim-meta">
          {filtered.length} of {entries.length}
        </span>
      </div>

      {error && <div className="admin-aim-error-banner">{error}</div>}

      <div className="admin-aim-table-wrap">
        <table className="admin-aim-table">
          <thead>
            <tr>
              <th>topic_id</th>
              <th>Title</th>
              <th>Summary</th>
              <th>Category</th>
              <th>Active</th>
              <th>v</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && entries.length === 0 ? (
              <tr>
                <td colSpan={8} className="admin-aim-empty">Lade…</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="admin-aim-empty">
                  Keine Einträge.
                </td>
              </tr>
            ) : (
              filtered.map((e) => (
                <tr
                  key={e.topic_id}
                  className={e.is_active ? '' : 'is-inactive'}
                >
                  <td>
                    <code>{e.topic_id}</code>
                  </td>
                  <td>{e.title}</td>
                  <td className="admin-aim-summary">{e.summary}</td>
                  <td className="admin-aim-meta">{e.category || 'general'}</td>
                  <td>{e.is_active ? '✓' : '—'}</td>
                  <td>{e.version}</td>
                  <td className="admin-aim-rel">{formatDate(e.updated_at)}</td>
                  <td>
                    <button
                      type="button"
                      className="admin-ai-btn admin-ai-btn--ghost"
                      onClick={() => setEditing(e)}
                    >
                      {repoManaged ? 'View' : 'Edit'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <KnowledgeEntryEditor
          mode={editing === 'new' ? 'create' : 'edit'}
          initial={editing === 'new' ? null : editing}
          canPublish={canPublish}
          readOnly={repoManaged}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={(result) => {
            setEditing(null)
            if (result?.queued) {
              setQueuedNotice(result.message || 'Submitted for review.')
            }
            refresh()
          }}
        />
      )}
    </div>
  )
}


/**
 * KnowledgeEntryEditor — modal create/edit form.
 *
 * topic_id is editable in create mode, locked in edit mode (immutable
 * post-creation by contract — past summaries / persisted convos may
 * reference it).
 */
export function KnowledgeEntryEditor({
  mode,
  initial,
  canPublish = true,
  readOnly = false,
  lockAppliesTo = null,
  categories = FALLBACK_CATEGORIES,
  onClose,
  onSaved,
}) {
  const isCreate = mode === 'create'
  const [topicId, setTopicId] = useState(initial?.topic_id || '')
  const [title, setTitle] = useState(initial?.title || '')
  const [summary, setSummary] = useState(initial?.summary || '')
  const [bodyMd, setBodyMd] = useState(initial?.body_md || '')
  const [keywords, setKeywords] = useState(initial?.keywords || '')
  // When opened from a flow's Knowledge tab we pre-tag the entry with that
  // branch slug. Locking only applies to NEW entries — editing keeps the
  // field free so an entry tagged for multiple branches never loses tokens.
  const [appliesTo, setAppliesTo] = useState(initial?.applies_to || lockAppliesTo || '')
  const appliesToLocked = !!lockAppliesTo && isCreate
  const [category, setCategory] = useState(initial?.category || 'general')
  const [isActive, setIsActive] = useState(initial?.is_active ?? true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const valid =
    topicId.length >= 2 &&
    TOPIC_ID_RE.test(topicId) &&
    title.length >= 2 &&
    summary.length >= 10 &&
    bodyMd.length >= 10

  // Concrete, user-facing reasons the submit button is disabled — so it's
  // never a mystery why it can't be clicked. topic_id is only checked in create
  // mode (it's locked + already valid when editing).
  const validationIssues = []
  if (isCreate && topicId.length < 2) {
    validationIssues.push('topic_id — enter at least 2 characters')
  } else if (isCreate && !TOPIC_ID_RE.test(topicId)) {
    validationIssues.push('topic_id — lowercase letters, digits, “_” or “-” only (no spaces or capitals)')
  }
  if (title.length < 2) validationIssues.push('Title — enter at least 2 characters')
  if (summary.length < 10) validationIssues.push(`Summary — needs ≥ 10 characters (currently ${summary.length})`)
  if (bodyMd.length < 10) validationIssues.push(`Body — needs ≥ 10 characters (currently ${bodyMd.length})`)

  const numericalWarning = /\b\d+(\.\d+)?\s*(Wh\/kg|Wh\/L|Ah|mΩ|°C)\b/.test(bodyMd)
  // Soft guidance only (§3 of the KB structure analysis) — matches
  // get_knowledge's own docstring guidance (~1500 tokens ≈ 6000 chars).
  // Never blocks save; the 20,000-char Pydantic cap is the only hard limit.
  const bodyTooLong = bodyMd.length > BODY_LENGTH_SOFT_WARN

  const handleSave = async () => {
    if (readOnly || !valid) return
    setSaving(true)
    setError(null)
    try {
      let result
      if (isCreate) {
        result = await createKnowledgeEntry({
          topic_id: topicId,
          title,
          summary,
          body_md: bodyMd,
          keywords,
          applies_to: appliesTo,
          category,
        })
      } else {
        result = await updateKnowledgeEntry(initial.topic_id, {
          title,
          summary,
          body_md: bodyMd,
          keywords,
          applies_to: appliesTo,
          category,
          is_active: isActive,
        })
      }
      // Authors get a queued proposal, not a live write. Hand the result to the
      // parent for a non-blocking notice + "My Submissions" pointer instead of
      // a blocking window.alert.
      onSaved(result)
    } catch (e) {
      setError(e?.message || 'network')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (readOnly) return
    if (!window.confirm(
      `Permanently delete entry ${initial.topic_id}? This removes it from the ` +
      `database entirely and CANNOT be undone. (To only hide it from the AI, ` +
      `set it inactive instead.)`,
    )) return
    setSaving(true)
    try {
      const result = await deleteKnowledgeEntry(initial.topic_id)
      onSaved(result)
    } catch (e) {
      setError(e?.message || 'network')
    } finally {
      setSaving(false)
    }
  }

  return createPortal(
    <div className="admin-aim-drawer-overlay" onClick={onClose}>
      <div
        className="admin-aim-drawer admin-aim-drawer--wide"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="admin-aim-drawer-header">
          <div className="admin-aim-drawer-headinfo">
            <div className="admin-aim-drawer-title">
              {isCreate
                ? 'New knowledge entry'
                : `${readOnly ? 'View' : 'Edit'} · ${initial.topic_id}`}
            </div>
          </div>
          <button
            type="button"
            className="admin-aim-icon-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="admin-aim-drawer-body">
          {readOnly && (
            <div className="admin-aim-admin-note">
              Read-only: this environment takes Ion knowledge from the
              repository file. Change it there in a merge request.
            </div>
          )}
          {!canPublish && !readOnly && (
            <div className="admin-aim-admin-note">
              Your changes are <strong>reviewed before reaching the AI</strong>.
              Saving submits a proposal; an approver publishes it. The live
              entry stays unchanged until then.
            </div>
          )}
          <div className="admin-aim-field">
            <label>topic_id</label>
            <input
              type="text"
              value={topicId}
              onInput={(e) => setTopicId(e.target.value)}
              disabled={!isCreate}
              placeholder="e_bike"
              className={
                isCreate && topicId && !TOPIC_ID_RE.test(topicId)
                  ? 'is-invalid'
                  : ''
              }
            />
            <div className="admin-aim-field-help">
              Stable id — kebab- or snake-case alphanumerics. Immutable
              after creation.
            </div>
          </div>

          <div className="admin-aim-field">
            <label>Title <span className="admin-aim-meta">({title.length}/120)</span></label>
            <input
              type="text"
              value={title}
              onInput={(e) => setTitle(e.target.value)}
              readOnly={readOnly}
              maxLength={120}
              placeholder="E-Bike Cell Selection"
            />
          </div>

          <div className="admin-aim-field">
            <label>
              Summary{' '}
              <span className="admin-aim-meta">({summary.length}/200)</span>
            </label>
            <textarea
              value={summary}
              onInput={(e) => setSummary(e.target.value)}
              readOnly={readOnly}
              maxLength={200}
              rows={2}
              placeholder="Action-oriented one-liner. The AI scans this to decide whether to fetch."
            />
            <div className="admin-aim-field-help">
              Keep terse and verb-led ("How to ...", "When to ...",
              "Tradeoffs of ..."). Goes into the cached prompt index.
            </div>
          </div>

          <div className="admin-aim-field">
            <label>
              Body (markdown){' '}
              <span className="admin-aim-meta">
                ({bodyMd.length} chars)
              </span>
            </label>
            <textarea
              value={bodyMd}
              onInput={(e) => setBodyMd(e.target.value)}
              readOnly={readOnly}
              rows={14}
              placeholder="## Heading&#10;&#10;Markdown body the AI receives via get_knowledge."
              className="admin-aim-monospace"
            />
            {numericalWarning && (
              <div className="admin-aim-warning">
                ⚠ Body contains specific cell numbers (Wh/kg, Ah, mΩ).
                Numerical claims about specific cells should come from
                tools, not knowledge entries — consider rephrasing.
              </div>
            )}
            {bodyTooLong && (
              <div className="admin-aim-warning">
                ⚠ Body is {bodyMd.length.toLocaleString()} chars — past the
                ~{BODY_LENGTH_SOFT_WARN.toLocaleString()}-char (~1500 token)
                guidance. It still fits the 20,000-char hard limit, but every
                fetch pays for it in full — consider splitting into sibling
                topic_ids sharing an <code>applies_to</code> tag so the model
                can fetch just the part it needs (or several at once via
                the batch <code>topic_ids</code> input on get_knowledge).
              </div>
            )}
          </div>

          <div className="admin-aim-field">
            <label>
              Keywords{' '}
              <span className="admin-aim-meta">
                (search/filter only — not in prompt)
              </span>
            </label>
            <input
              type="text"
              value={keywords}
              onInput={(e) => setKeywords(e.target.value)}
              readOnly={readOnly}
              maxLength={400}
              placeholder="bicycle, e-bike, pedelec"
            />
          </div>

          <div className="admin-aim-field">
            <label>applies_to</label>
            <input
              type="text"
              value={appliesTo}
              onInput={(e) => setAppliesTo(e.target.value)}
              maxLength={200}
              placeholder="benchmarking, cells"
              disabled={appliesToLocked}
              readOnly={readOnly}
            />
            <div className="admin-aim-field-help">
              {appliesToLocked ? (
                <>
                  Scoped to the <code>{lockAppliesTo}</code> flow — this entry is
                  prioritised in the assistant's index whenever that branch is
                  active.
                </>
              ) : (
                <>
                  Comma-separated list of branch/context slugs. When it tags a
                  selection-flow branch (e.g. <code>power_tool</code>), the entry
                  is prioritised in the assistant's index for that branch.
                </>
              )}
            </div>
          </div>

          <div className="admin-aim-field">
            <label>Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={readOnly}
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <div className="admin-aim-field-help">
              Admin-plane filtering/reporting only — NOT read by the AI at
              runtime (distinct from <code>applies_to</code>, which is).
            </div>
          </div>

          {!isCreate && (
            <div className="admin-aim-field">
              <label className="admin-aim-checkbox">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={readOnly}
                />
                Active (appears in AI's index)
              </label>
            </div>
          )}

          {error && <div className="admin-aim-error-banner">{error}</div>}
        </div>

        {!readOnly && validationIssues.length > 0 && (
          <div className="admin-aim-validation" role="status">
            <span className="admin-aim-validation-title">
              To {canPublish ? (isCreate ? 'create' : 'save') : 'submit'}, complete:
            </span>
            <ul>
              {validationIssues.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        )}

        <footer className="admin-aim-drawer-footer">
          {!isCreate && !readOnly && (
            <button
              type="button"
              className="admin-ai-btn admin-ai-btn--danger"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete
            </button>
          )}
          <span className="admin-aim-spacer" />
          <button
            type="button"
            className="admin-ai-btn admin-ai-btn--ghost"
            onClick={onClose}
            disabled={saving}
          >
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
            <button
              type="button"
              className="admin-ai-btn"
              onClick={handleSave}
              disabled={!valid || saving}
            >
              {saving
                ? 'Saving…'
                : canPublish
                  ? isCreate
                    ? 'Create'
                    : 'Save'
                  : 'Submit for review'}
            </button>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  )
}
